import { createSlice } from "@reduxjs/toolkit";

import Nullable from "../../../types/Nullable";
import TableSortMethod from "../../../types/SortMethod";
import TableWidths from "../../../types/TableWidths";

export interface TableColumn<ID extends string = string> {
	id: ID;
	label: string;
	sortable?: boolean;
	resizable?: boolean;
	fixed?: boolean;
	width?: number;
}

type RecordOfTables<Data> = Record<string, Nullable<Data>>;

interface TableColumnsState {
	visible: RecordOfTables<string[]>;
	sortMethod: RecordOfTables<TableSortMethod>;
	widths: RecordOfTables<TableWidths>;
	/**
	 * New columns already added once to a saved column list, so that a column
	 * the dispatcher hides later is not added again.
	 */
	addedColumns?: RecordOfTables<string[]>;
}

const initialState: TableColumnsState = {
	visible: {},
	sortMethod: {},
	widths: {},
	addedColumns: {},
};

/**
 * Columns added after dispatchers already saved their column lists. A saved
 * list gets them once (see `addNewColumns`); a list the dispatcher saves after
 * that is final — a new column missing from it was hidden on purpose.
 */
export const NEW_COLUMNS: { id: string; after?: string }[] = [
	{ id: "paymentStatus", after: "paymentType" },
];

function setVisibleColumns(
	state: TableColumnsState,
	action: {
		payload: {
			uid: string;
			columns: string[];
		};
		type: string;
	},
) {
	state.visible[action.payload.uid] = action.payload.columns;
	state.addedColumns ??= {};
	state.addedColumns[action.payload.uid] = NEW_COLUMNS.map(
		(column) => column.id,
	);
}

function addNewColumns(
	state: TableColumnsState,
	action: {
		payload: {
			uid: string;
			columns: string[];
			added: string[];
		};
		type: string;
	},
) {
	state.visible[action.payload.uid] = action.payload.columns;
	state.addedColumns ??= {};
	state.addedColumns[action.payload.uid] = [
		...(state.addedColumns[action.payload.uid] ?? []),
		...action.payload.added,
	];
}

function setSortMethod(
	state: TableColumnsState,
	action: {
		payload: {
			uid: string;
			method: TableSortMethod;
		};
		type: string;
	},
) {
	state.sortMethod[action.payload.uid] = {
		...state.sortMethod[action.payload.uid],
		...action.payload.method,
	};
}

function setWidths(
	state: TableColumnsState,
	action: {
		payload: {
			uid: string;
			widths: TableWidths;
		};
		type: string;
	},
) {
	state.widths[action.payload.uid] = action.payload.widths;
}

function setAll(
	state: TableColumnsState,
	action: {
		payload: TableColumnsState;
		type: string;
	},
) {
	state.sortMethod = action.payload.sortMethod;
	state.widths = action.payload.widths;
	state.visible = action.payload.visible;
	state.addedColumns =
		action.payload.addedColumns ?? state.addedColumns ?? {};
}

// Slice //

const tableColumns = createSlice({
	name: "tableColumns",
	initialState,
	reducers: {
		setVisibleColumns,
		addNewColumns,
		setSortMethod,
		setWidths,
		setAll,
	},
});

export default tableColumns;
